/**
 * Emails de Amazon (amazon.es) → pedidos, envíos y reembolsos (puro). Lee el texto del email, no el HTML.
 * Lo usan el servidor (al llegar cada email) y el dispositivo (los que el servidor no pudo apuntar y los .eml subidos).
 * - Pedido (auto-confirm): uno o varios «Pedido n.º» en el mismo email, cada uno con sus artículos («Cantidad: 2», «13.5 EUR»
 *   es el precio de cada uno) y su «Total».
 * - Enviado (confirmar-envio): igual, con lo que va en ese paquete. Amazon cobra al enviar, así que es lo que sale en el banco.
 * - Reembolso («Se emitió tu reembolso»): «N.º de pedido», «Reembolso total* 123,00 €» y los artículos devueltos (sin precio).
 * - En reparto, entregado, nueva fecha, devolución recibida…: avisos sin dinero; no se apuntan.
 * Solo se guarda lo necesario para repartir los cobros: número de pedido, fecha, artículos, cantidades e importes.
 */
import { round2 } from '@pf/domain';
import type { OrderKind, OrderRow } from './parse.js';

export type AmazonKind = OrderKind | 'out-for-delivery' | 'delivered' | 'eta' | 'return' | 'cancelled' | 'other';
export interface AmazonItem { name: string; qty: number; amount: number | null }
export interface AmazonPart { orderId: string; items: AmazonItem[]; total: number | null }
export interface AmazonMail { kind: AmazonKind; date: string; parts: AmazonPart[] }

export const AMAZON_KIND_LABEL: Record<AmazonKind, string> = {
  order: 'Pedido', shipment: 'Enviado', refund: 'Reembolso', 'out-for-delivery': 'En reparto', delivered: 'Entregado',
  eta: 'Nueva fecha de entrega', return: 'Devolución', cancelled: 'Cancelado', other: 'Otro aviso',
};

const ORDER_ID = /\b(\d{3}-\d{7}-\d{7})\b/;

/** «9.7», «13.50», «1,234.56» (EUR) o «123,00», «1.234,56» (€) → número. */
export function amazonAmount(s: string): number | null {
  const t = s.replace(/\s/g, '');
  const eu = /,\d{1,2}$/.test(t);
  const n = Number(eu ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, ''));
  return Number.isFinite(n) ? round2(n) : null;
}

/** El nombre del artículo, corto: hasta el primer « | » y como mucho 100 letras. */
export const shortName = (s: string) => s.replace(/\s+/g, ' ').split(' | ')[0]!.trim().slice(0, 100);

export function amazonKind(subject: string, from: string, text = ''): AmazonKind {
  const s = subject.toLowerCase(), f = from.toLowerCase();
  if (/cancelad|cancelaci/.test(s)) return 'cancelled';
  if (/^devolucion@/.test(f) || /reembolso|devoluci/.test(s)) return /se emiti[óo] tu reembolso|hemos reembolsado/i.test(text) ? 'refund' : 'return';
  if (/^auto-confirm@/.test(f) || /^pedido\b/.test(s)) return 'order';
  if (/^confirmar-envio@/.test(f) || /^enviado\b/.test(s)) return 'shipment';
  if (/^en reparto\b/.test(s)) return 'out-for-delivery';
  if (/^entregado\b/.test(s)) return 'delivered';
  if (/fecha estimada de entrega|actualizaci[óo]n/.test(s)) return 'eta';
  return 'other';
}

/** Artículos con «Cantidad» y, si lo dice, el precio de cada uno («* Nombre / Cantidad: 2 / 13.5 EUR»). */
function listItems(block: string): AmazonItem[] {
  const out: AmazonItem[] = [];
  for (const m of block.matchAll(/^\* (.+)\n\s*Cantidad: (\d+)(?:\n\s*([\d.,]+) EUR)?/gm)) {
    const qty = Number(m[2]), unit = m[3] ? amazonAmount(m[3]) : null;
    out.push({ name: shortName(m[1]!), qty, amount: unit === null ? null : round2(unit * qty) });
  }
  return out;
}

/** Pedido o envío: un trozo por cada «Pedido n.º», con sus artículos y su total. */
function orderParts(text: string): AmazonPart[] {
  const chunks = text.split(/Pedido n\.º/).slice(1);
  return chunks.flatMap(c => {
    const orderId = ORDER_ID.exec(c)?.[1];
    if (!orderId) return [];
    const total = /^Total\s*\n\s*([\d.,]+) EUR/m.exec(c)?.[1];
    return [{ orderId, items: listItems(c), total: total ? amazonAmount(total) : null }];
  });
}

function refundPart(text: string): AmazonPart[] {
  const orderId = /N\.º de pedido\s+(\d{3}-\d{7}-\d{7})/.exec(text)?.[1] ?? ORDER_ID.exec(text)?.[1];
  const total = /Reembolso total\*?\s+([\d.]+,\d{2})\s*€/.exec(text)?.[1] ?? /Hemos reembolsado ([\d.]+,\d{2})\s*€/.exec(text)?.[1];
  if (!orderId) return [];
  const items = [...text.matchAll(/\[([^\]]+)\]\(https?:\/\/www\.amazon\.[a-z.]+\/gp\/product\/[^)]*\)\s*\n?\s*Cantidad: (\d+)/g)]
    .map(m => ({ name: shortName(m[1]!), qty: Number(m[2]), amount: null }));
  return [{ orderId, items, total: total ? amazonAmount(total) : null }];
}

/** Lo que dice un email de Amazon. date: el día que llegó (aaaa-mm-dd). */
export function parseAmazonMail(m: { subject: string; from: string; date: string; text: string }): AmazonMail {
  const text = m.text.replace(/\r\n/g, '\n');
  const kind = amazonKind(m.subject, m.from, text);
  const parts = kind === 'refund' ? refundPart(text) : kind === 'other' ? [] : orderParts(text);
  return { kind, date: m.date.slice(0, 10), parts };
}

/** Filas de _Pedidos de un email: solo pedidos, envíos y reembolsos con artículos. La Ref no cambia si llega dos veces. */
export function orderRows(mail: AmazonMail): Array<Omit<OrderRow, 'row'>> {
  if (mail.kind !== 'order' && mail.kind !== 'shipment' && mail.kind !== 'refund') return [];
  const kind = mail.kind;
  return mail.parts.filter(p => p.items.length).flatMap(p => {
    const ref = kind === 'order' ? `pedido:${p.orderId}`
      : `${kind === 'shipment' ? 'envio' : 'reembolso'}:${p.orderId}:${p.total?.toFixed(2) ?? ''}:${mail.date}`;
    return p.items.map(x => ({ date: mail.date, kind, orderId: p.orderId, item: x.name, qty: x.qty, amount: x.amount, total: p.total, ref, movementId: '' }));
  });
}
